import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import WaitlistForm from "@/components/landing/WaitlistForm";
import { api } from "@/lib/api";

function Counter({ to = 0, duration = 1500, className = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!to) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <span className={className}>{n.toLocaleString("en-IN")}</span>;
}

export default function Hero() {
  const [waitCount, setWaitCount] = useState(2147);

  useEffect(() => {
    api
      .get("/waitlist/count")
      .then((r) => setWaitCount(r.data.count))
      .catch(() => {});
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute -top-20 right-0 h-[480px] w-[480px] rounded-full bg-[#084392]/10 blur-3xl pointer-events-none float-slow" />
      <div className="absolute top-1/3 -left-32 h-[360px] w-[360px] rounded-full bg-[#084392]/8 blur-3xl pointer-events-none float-med" />

      {/* Floating tick bars — top left */}
      <div
        aria-hidden
        className="hidden lg:flex absolute top-32 left-10 items-end gap-1 opacity-70 pointer-events-none"
      >
        {[14, 22, 18, 28, 20, 32].map((h, i) => (
          <div
            key={i}
            className="w-1.5 rounded-sm bg-[#084392] tick-bar"
            style={{ height: `${h}px`, animationDelay: `${i * 0.18}s` }}
          />
        ))}
      </div>

      {/* Floating candlestick — bottom right */}
      <div
        aria-hidden
        className="hidden lg:block absolute bottom-28 right-16 pointer-events-none float-med"
      >
        <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
          <line x1="14" y1="10" x2="14" y2="60" stroke="#10b981" strokeWidth="2" />
          <rect x="8" y="22" width="12" height="28" fill="#10b981" rx="1" />
          <line x1="36" y1="18" x2="36" y2="52" stroke="#ef4444" strokeWidth="2" />
          <rect x="30" y="26" width="12" height="20" fill="#ef4444" rx="1" />
          <line x1="58" y1="8" x2="58" y2="50" stroke="#10b981" strokeWidth="2" />
          <rect x="52" y="14" width="12" height="30" fill="#10b981" rx="1" />
        </svg>
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-semibold text-[#084392]"
          >
            <Sparkles size={14} />
            Institution-grade automation, now for retail traders
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="hero-title"
            className="font-heading mt-6 text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.98]"
          >
            Automate trades
            <br />
            as simply as
            <br />
            <span className="text-[#084392]">scheduling groceries.</span>
            <span className="caret text-[#084392]">_</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-subtitle"
            className="mt-6 max-w-2xl text-lg md:text-xl text-[#475569] leading-relaxed"
          >
            Tradecure is a <b className="text-[#0f172a]">no-code, no-prompt</b> algo
            platform built for the 90% of traders who don&rsquo;t code, don&rsquo;t watch
            screens all day, and just want discipline without the jargon. Set
            risk in rupees. Let the market do its thing.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 max-w-lg"
          >
            <WaitlistForm source="hero" />
            <div className="mt-3 flex items-center gap-3 text-sm text-[#475569]">
              <div className="flex -space-x-2">
                {["#084392", "#06326e", "#10b981", "#1e40af"].map((c, i) => (
                  <div
                    key={i}
                    className="h-6 w-6 rounded-full border-2 border-white"
                    style={{ background: c }}
                  />
                ))}
              </div>
              <span>
                <Counter to={waitCount} className="font-mono font-bold text-[#0f172a]" /> traders already in line
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[#475569]"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#10b981]" />
              SEBI-regulated brokers
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-[#10b981] pulse-dot" />
              Live execution in &lt; 40ms
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-[#084392]" />
              Risk in ₹, not Greeks
            </div>
          </motion.div>
        </div>

        {/* Right: Visual — strategy card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-5"
          data-testid="hero-visual"
        >
          <HeroVisual />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div
        aria-hidden
        className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 pointer-events-none"
      >
        <span className="font-mono text-[10px] tracking-[0.25em] text-[#475569]">
          SCROLL
        </span>
        <div className="h-8 w-[22px] rounded-full border border-[#084392]/40 flex justify-center pt-1.5">
          <span className="h-1.5 w-1 rounded-full bg-[#084392] scroll-bounce" />
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  const ref = useRef(null);
  // Raw pointer offset in range [-1, 1] from center (x & y)
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  // Smooth springs so the motion feels premium / fluid
  const springConf = { stiffness: 160, damping: 18, mass: 0.6 };
  const smx = useSpring(mx, springConf);
  const smy = useSpring(my, springConf);

  // REPEL: when pointer is at top (my = -1), top should go BACK → rotateX = -(my) * maxAngle
  // When pointer is at right (mx = +1), right should go BACK → rotateY = -(mx) * maxAngle
  const MAX = 12; // degrees
  const rotateX = useTransform(smy, (v) => -v * MAX);
  const rotateY = useTransform(smx, (v) => v * MAX);

  // Counter-highlight: the quadrant OPPOSITE the cursor lifts forward (positive Z).
  // We fake it with a radial highlight gradient positioned at the OPPOSITE corner.
  const highlightX = useTransform(smx, (v) => `${50 - v * 50}%`);
  const highlightY = useTransform(smy, (v) => `${50 - v * 50}%`);

  // Parallax inner layers — glint follows cursor slightly; content leans opposite for depth
  const innerX = useTransform(smx, (v) => v * 10);
  const innerY = useTransform(smy, (v) => v * 10);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;   // 0..1
    const y = (e.clientY - r.top) / r.height;   // 0..1
    mx.set(x * 2 - 1);                          // -1..1
    my.set(y * 2 - 1);
  };

  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div className="relative" style={{ perspective: 1400 }}>
      <div className="absolute -inset-4 bg-gradient-to-br from-[#084392]/10 via-transparent to-[#084392]/5 rounded-3xl blur-2xl" />

      <motion.div
        ref={ref}
        data-testid="hero-strategy-card"
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        className="relative rounded-2xl border border-[#e2e8f0] bg-white shadow-[0_30px_80px_-30px_rgba(8,67,146,0.25)] overflow-hidden"
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] bg-[#f8fafc]">
          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-full bg-[#ef4444]/70" />
            <div className="h-2.5 w-2.5 rounded-full bg-[#f59e0b]/70" />
            <div className="h-2.5 w-2.5 rounded-full bg-[#10b981]/70" />
          </div>
          <div className="font-mono text-xs text-[#475569]">tradecure.app / nifty-momentum</div>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#10b981]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10b981] pulse-dot" />
            LIVE
          </div>
        </div>

        {/* Moving light — the "lifted" opposite quadrant */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: useTransform(
              [highlightX, highlightY],
              ([hx, hy]) =>
                `radial-gradient(420px circle at ${hx} ${hy}, rgba(8,67,146,0.10), transparent 55%)`
            ),
            mixBlendMode: "multiply",
          }}
        />

        {/* Content */}
        <motion.div
          className="p-5 md:p-6 space-y-5 relative"
          style={{ x: innerX, y: innerY, transformStyle: "preserve-3d" }}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-mono text-[#475569]">STRATEGY</div>
              <div className="font-heading text-xl font-bold">Nifty Momentum Pro</div>
            </div>
            <div className="rounded-md bg-[#10b981]/10 text-[#10b981] text-xs font-bold px-2.5 py-1">
              +2.4% today
            </div>
          </div>

          {/* Chart */}
          <div className="h-40 rounded-xl bg-[#f8fafc] border border-[#e2e8f0] p-3 relative overflow-hidden">
            <svg viewBox="0 0 400 140" className="w-full h-full">
              <defs>
                <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#084392" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#084392" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0 100 L40 85 L80 95 L120 70 L160 78 L200 55 L240 62 L280 40 L320 48 L360 25 L400 32 L400 140 L0 140 Z"
                fill="url(#g1)"
              />
              <path
                className="draw-line"
                d="M0 100 L40 85 L80 95 L120 70 L160 78 L200 55 L240 62 L280 40 L320 48 L360 25 L400 32"
                stroke="#084392"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="400" cy="32" r="5" fill="#084392" />
              <circle cx="400" cy="32" r="10" fill="#084392" opacity="0.25" className="pulse-dot" />
            </svg>
          </div>

          {/* Legs */}
          <div className="space-y-2">
            {[
              { side: "BUY", inst: "NIFTY 24500 CE", qty: 50, pnl: "+₹1,240", ok: true },
              { side: "SELL", inst: "NIFTY 24700 CE", qty: 50, pnl: "+₹820", ok: true },
              { side: "BUY", inst: "NIFTY 24300 PE", qty: 25, pnl: "−₹180", ok: false },
            ].map((leg, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-lg border border-[#e2e8f0] px-3 py-2.5"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded ${
                      leg.side === "BUY"
                        ? "bg-[#084392]/10 text-[#084392]"
                        : "bg-[#ef4444]/10 text-[#ef4444]"
                    }`}
                  >
                    {leg.side}
                  </span>
                  <div>
                    <div className="text-sm font-semibold">{leg.inst}</div>
                    <div className="text-xs text-[#475569] font-mono">qty {leg.qty}</div>
                  </div>
                </div>
                <div
                  className={`text-sm font-bold ${
                    leg.ok ? "text-[#10b981]" : "text-[#ef4444]"
                  }`}
                >
                  {leg.pnl}
                </div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <div className="rounded-lg border border-[#e2e8f0] p-3">
              <div className="text-[10px] font-mono text-[#475569]">MAX LOSS</div>
              <div className="font-bold text-[#0f172a]">₹2,500</div>
            </div>
            <div className="rounded-lg border border-[#e2e8f0] p-3">
              <div className="text-[10px] font-mono text-[#475569]">TARGET</div>
              <div className="font-bold text-[#10b981]">₹6,000</div>
            </div>
            <div className="rounded-lg border border-[#e2e8f0] p-3">
              <div className="text-[10px] font-mono text-[#475569]">AUTO-ADJUST</div>
              <div className="font-bold text-[#084392] flex items-center gap-1">
                ON <ArrowRight size={12} />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
